import { useMemo, useState } from 'react';
import { Plus, Search, ShieldCheck } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Input } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useVpnGateways, useCreateVpnGateway, useDeleteVpnGateway, type VpnGateway } from '@/api/opennebula';
import { RowMenu, SimpleCreateDialog } from '../components/CrudBits';
import { GatewayDetailDialog, GatewayEditDialog } from '../VpnGatewayDetail';
import { CountFooter, ListSkeleton } from './GenericList';

// Each VPN resource tab is its own component so its hooks run unconditionally
// (calling hooks inside the parent's conditional JSX violates the Rules of Hooks).
export function GatewaysTab({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const { data: gateways = [], isLoading } = useVpnGateways(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<VpnGateway | null>(null);
  const [edit, setEdit] = useState<VpnGateway | null>(null);
  const [del, setDel] = useState<VpnGateway | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return gateways;
    return gateways.filter((g) => [g.name, g.id, g.public_ip, g.status].some((f) => String(f ?? '').toLowerCase().includes(q)));
  }, [gateways, query]);

  const create = useCreateVpnGateway(serviceId);
  const remove = useDeleteVpnGateway(serviceId);

  const columns: ColumnDef<VpnGateway, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <b style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</b> },
    { id: 'ip', header: 'Public IP', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.public_ip || '—'}</span> },
    { id: 'state', header: 'State', cell: ({ row }) => <StatusPill tone={row.original.status === 'ready' ? 'success' : 'muted'}>{row.original.status || 'Unknown'}</StatusPill> },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'View detail', onSelect: () => setDetail(row.original) }, { label: 'Edit', onSelect: () => setEdit(row.original) }, { label: 'Delete', danger: true, onSelect: () => setDel(row.original) }]} /> },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder="Search gateways" startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 360 }} />}
        end={<Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New gateway</Button>}
      />
      {isLoading ? <ListSkeleton /> : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(g) => String(g.id)}
          onRowClick={(g) => setDetail(g)}
          footer={filtered.length ? <CountFooter n={filtered.length} noun="VPN gateways" /> : undefined}
          emptyState={
            gateways.length === 0 ? (
              <EmptyState
                icon={<ShieldCheck size={28} />}
                title="No VPN gateways yet"
                description="Create a gateway to establish site-to-site VPN tunnels."
                actions={<Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New gateway</Button>}
              />
            ) : (
              'No gateways match your search.'
            )
          }
        />
      )}
      <SimpleCreateDialog open={createOpen} onOpenChange={setCreateOpen} title="New VPN gateway"
        fields={[{ key: 'name', label: 'Name', placeholder: 'vpn-gw' }]}
        onCreate={{ mutate: (p) => create.mutate(p, { onSuccess: () => { show({ tone: 'success', title: 'Gateway created' }); setCreateOpen(false); }, onError: () => show({ tone: 'danger', title: 'Create failed' }) }), isPending: create.isPending }} />
      {detail && <GatewayDetailDialog serviceId={serviceId} gateway={detail} onClose={() => setDetail(null)} />}
      {edit && <GatewayEditDialog serviceId={serviceId} gateway={edit} onClose={() => setEdit(null)} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete VPN gateway?" description={`Remove “${del?.name || del?.id}”?`} confirmLabel="Delete" destructive loading={remove.isPending}
        onConfirm={() => del && remove.mutate(del.id, { onSuccess: () => { show({ tone: 'success', title: 'Gateway deleted' }); setDel(null); } })} />
    </>
  );
}
